import { useId, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';

import styles from './primitives.module.css';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' };

export function Button({ variant = 'primary', className, type = 'button', ...props }: ButtonProps) {
  return <button type={type} className={[styles.button, styles[variant], className].filter(Boolean).join(' ')} {...props} />;
}

type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & { label: string; hint?: string; error?: string | null };

export function TextField({ label, hint, error, ...input }: TextFieldProps) {
  const id = useId();
  const describedBy = [hint ? `${id}-hint` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ') || undefined;
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>{label}</label>
      <input id={id} className={styles.input} aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...input} />
      {hint ? <div id={`${id}-hint`} className={styles.hint}>{hint}</div> : null}
      {error ? <div id={`${id}-error`} className={styles.error}>{error}</div> : null}
    </div>
  );
}

type SwitchProps = { label: string; description: ReactNode; checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean; plain?: boolean };

// A labelled on/off switch (role="switch"), operable by mouse, touch and keyboard.
export function Switch({ label, description, checked, onChange, disabled, plain }: SwitchProps) {
  const id = useId();
  return (
    <div className={[styles.switchRow, plain ? styles.switchPlain : ''].join(' ')}>
      <div className={styles.switchText}>
        <div id={`${id}-label`} className={styles.switchLabel}>{label}</div>
        <div id={`${id}-description`} className={styles.hint}>{description}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-description`}
        disabled={disabled}
        className={styles.switch}
        onClick={() => onChange(!checked)}
      >
        <span className={styles.thumb} aria-hidden="true" />
      </button>
    </div>
  );
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'good' | 'warning' | 'danger'; children: ReactNode }) {
  return <div className={[styles.notice, styles[tone]].join(' ')} role={tone === 'danger' ? 'alert' : 'status'}>{children}</div>;
}

type SelectFieldProps = { label: string; value: string; onChange: (value: string) => void; options: { value: string; label: string }[]; hint?: string };

export function SelectField({ label, value, onChange, options, hint }: SelectFieldProps) {
  const id = useId();
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>{label}</label>
      <select id={id} className={styles.input} value={value} onChange={event => onChange(event.target.value)} aria-describedby={hint ? `${id}-hint` : undefined}>
        {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
      {hint ? <div id={`${id}-hint`} className={styles.hint}>{hint}</div> : null}
    </div>
  );
}

type TextAreaFieldProps = { label: string; value: string; onChange: (value: string) => void; hint?: string; error?: string | null; maxLength?: number; rows?: number; placeholder?: string };

export function TextAreaField({ label, value, onChange, hint, error, maxLength, rows = 4, placeholder }: TextAreaFieldProps) {
  const id = useId();
  const describedBy = [hint ? `${id}-hint` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ') || undefined;
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>{label}</label>
      <textarea id={id} className={styles.textarea} value={value} rows={rows} maxLength={maxLength} placeholder={placeholder} onChange={event => onChange(event.target.value)} aria-invalid={error ? true : undefined} aria-describedby={describedBy} />
      {hint ? <div id={`${id}-hint`} className={styles.hint}>{hint}{maxLength ? ` ${value.length}/${maxLength}` : ''}</div> : null}
      {error ? <div id={`${id}-error`} className={styles.error}>{error}</div> : null}
    </div>
  );
}

// Design monogram colours: a person, shop or guild keeps the same one.
const MONOGRAM_COLOURS = ['#9C5B2E', '#0F766E', '#B45309', '#9A3412', '#6D28D9', '#0E7490', '#4D7C0F', '#BE185D', '#1D4ED8', '#9333EA', '#0369A1', '#BE123C'];

export function monogramColour(name: string): string {
  let hash = 0;
  for (const character of name) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  return MONOGRAM_COLOURS[hash % MONOGRAM_COLOURS.length]!;
}

// Design: an empty banner on someone else's page is their monogram colour, 35% lighter.
export function bannerColour(name: string): string {
  const value = parseInt(monogramColour(name).slice(1), 16);
  const mix = (channel: number) => Math.round(channel + (255 - channel) * 0.35);
  return `rgb(${mix(value >> 16)}, ${mix((value >> 8) & 255)}, ${mix(value & 255)})`;
}

export function Avatar({ name, photoUrl, size = 48, square = false }: { name: string; photoUrl?: string | null; size?: number; square?: boolean }) {
  const initials = name.split(/[\s.]+/).filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase()).join('') || '?';
  const radius = square ? Math.round(size * 0.29) : '50%';
  return photoUrl
    ? <img className={styles.avatar} src={photoUrl} alt="" width={size} height={size} style={{ width: size, height: size, borderRadius: radius }} />
    : <span className={styles.avatar} data-avatar="" aria-hidden="true" style={{ width: size, height: size, fontSize: Math.max(11, Math.round(size * (size < 40 ? 0.3 : 0.32))), borderRadius: radius, background: monogramColour(name) }}>{initials}</span>;
}
